import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle,
  XCircle,
  CreditCard,
  Truck,
  Phone,
  MapPin,
  Brain,
  Loader2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ConnectorHealthPage() {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/connector-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setHealth(await response.json());
      }
    } catch (error) {
      console.error('Error fetching health:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const connectors = health ? [
    {
      name: 'Stripe Payments',
      icon: CreditCard,
      enabled: health.stripe?.enabled,
      configured: health.stripe?.configured,
      details: 'Test mode active',
      description: 'Payment processing for deposits',
      lastCallSuccess: health.stripe?.last_call_success,
      lastCallTime: health.stripe?.last_call_time
    },
    {
      name: 'Central Dispatch',
      icon: Truck,
      enabled: health.central_dispatch?.enabled,
      configured: health.central_dispatch?.configured,
      details: health.central_dispatch?.base_url || 'Not configured',
      description: 'Load board integration for carrier matching',
      lastCallSuccess: health.central_dispatch?.last_call_success,
      lastCallTime: health.central_dispatch?.last_call_time
    },
    {
      name: 'Voice Calls',
      icon: Phone,
      enabled: health.voice_calls?.enabled,
      configured: false,
      details: health.voice_calls?.enabled ? 'Ready' : 'Disabled',
      description: 'AI voice calls for lead follow-up'
    },
    {
      name: 'Google Distance API',
      icon: MapPin,
      enabled: health.google_distance?.enabled,
      configured: false,
      details: health.google_distance?.enabled ? 'Ready' : 'Using estimation',
      description: 'Accurate distance calculations'
    },
    {
      name: 'LLM Provider',
      icon: Brain,
      enabled: true,
      configured: health.llm?.configured,
      details: `${health.llm?.provider} / ${health.llm?.model}`,
      description: 'AI sales engine for customer conversations'
    }
  ] : [];

  const formatTime = (isoString) => {
    if (!isoString) return 'Never';
    return new Date(isoString).toLocaleString();
  };

  return (
      <div className="space-y-6" data-testid="admin-connector-health">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-semibold">Connector Health</h1>
            <p className="text-muted-foreground">Status of external integrations and services</p>
          </div>
          <Button variant="outline" size="icon" onClick={fetchHealth}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Error Queue Alert */}
        {health?.error_queue_count > 0 && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-medium text-red-500">Error Queue Alert</p>
              <p className="text-sm text-muted-foreground">
                {health.error_queue_count} unresolved error(s) in queue
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connectors.map((connector) => (
              <Card key={connector.name} className="bg-card border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        connector.enabled && connector.configured 
                          ? 'bg-green-500/10' 
                          : connector.enabled 
                            ? 'bg-yellow-500/10' 
                            : 'bg-muted'
                      }`}>
                        <connector.icon className={`h-5 w-5 ${
                          connector.enabled && connector.configured 
                            ? 'text-green-500' 
                            : connector.enabled 
                              ? 'text-yellow-500' 
                              : 'text-muted-foreground'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold">{connector.name}</h3>
                        <p className="text-xs text-muted-foreground">{connector.description}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Enabled</span>
                      {connector.enabled ? (
                        <Badge className="bg-green-500/10 text-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge className="bg-muted text-muted-foreground">
                          <XCircle className="h-3 w-3 mr-1" />
                          No
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Configured</span>
                      {connector.configured ? (
                        <Badge className="bg-green-500/10 text-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Yes
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500/10 text-yellow-500">
                          <XCircle className="h-3 w-3 mr-1" />
                          No
                        </Badge>
                      )}
                    </div>

                    {connector.lastCallSuccess !== undefined && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Last Call</span>
                        {connector.lastCallSuccess ? (
                          <Badge className="bg-green-500/10 text-green-500">Success</Badge>
                        ) : connector.lastCallSuccess === false ? (
                          <Badge className="bg-red-500/10 text-red-500">Failed</Badge>
                        ) : (
                          <Badge className="bg-muted text-muted-foreground">N/A</Badge>
                        )}
                      </div>
                    )}

                    <div className="pt-3 border-t border-white/5">
                      <p className="text-xs text-muted-foreground">{connector.details}</p>
                      {connector.lastCallTime && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last: {formatTime(connector.lastCallTime)}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Feature Toggles Info */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <CardTitle className="font-outfit text-lg">Feature Toggles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground space-y-2">
              <p>Feature toggles are controlled via environment variables:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><code className="text-foreground">VOICE_CALLS_ENABLED</code> - Enable AI voice calls for follow-ups</li>
                <li><code className="text-foreground">CENTRAL_DISPATCH_ENABLED</code> - Enable Central Dispatch integration</li>
                <li><code className="text-foreground">GOOGLE_DISTANCE_API_ENABLED</code> - Use Google for distance calculations</li>
                <li><code className="text-foreground">ALERT_EMAIL_ENABLED</code> - Send KPI alert emails to admin</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Error Queue Count */}
        <Card className="bg-card border-white/5">
          <CardHeader>
            <CardTitle className="font-outfit text-lg">System Health</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Error Queue</p>
                <p className="font-semibold">{health?.error_queue_count || 0} unresolved errors</p>
              </div>
              {health?.error_queue_count > 0 && (
                <Badge className="bg-red-500/10 text-red-500">Needs Attention</Badge>
              )}
              {health?.error_queue_count === 0 && (
                <Badge className="bg-green-500/10 text-green-500">All Clear</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
  );
}
